/**
 * AI Assistant visibility + shared credit pool (AI Assistant + Data Enrichment).
 * Matches WEB-CRM: show when canUseAI !== false AND tenant aiAssistantEnabled !== false.
 * Exhausted credits still show the launcher; click opens the usage-limit modal.
 * Fail-open: if summary/user cannot be fetched, the assistant stays available.
 */

import { FetchUtils } from '../utils/fetch-utils.js';
import { Logger } from '../utils/logger.js';
import { storeTokens } from '../auth.js';
import { t } from '../../i18n.js';

let cachedSummary = null;
/** Missing/undefined treated as allowed (same grandfathering as WEB-CRM). */
let cachedCanUseAI = true;
let userPermissionLoaded = false;
let userPermissionPromise = null;

export function isCreditLimitMessage(message) {
  return typeof message === 'string' && /limit reached/i.test(message);
}

export function isAiCreditLimitReached() {
  if (!cachedSummary?.combined) return false;
  const { used, limit } = cachedSummary.combined;
  return limit > 0 && used >= limit;
}

export function isAiAssistantEnabled() {
  if (!cachedSummary) return true;
  return cachedSummary.aiAssistantEnabled !== false;
}

/** Per-user permission. Missing/undefined is allowed. */
export function canUseAI() {
  return cachedCanUseAI !== false;
}

/** Same gate as WEB-CRM RightItems.shouldShowAIAssistant. */
export function shouldShowAiAssistant() {
  return canUseAI() && isAiAssistantEnabled();
}

export async function loadAiCreditSummary() {
  try {
    cachedSummary = await FetchUtils.get('/api/ai/credit-summary');
  } catch (error) {
    Logger.warn('Failed to load AI credit summary; failing open', error);
    cachedSummary = null;
  }
  return cachedSummary;
}

export async function loadCanUseAI() {
  if (userPermissionLoaded) return cachedCanUseAI;
  if (userPermissionPromise) return userPermissionPromise;

  userPermissionPromise = (async () => {
    try {
      const response = await FetchUtils.get('/api/user/refresh');
      const user = response?.user || response;
      cachedCanUseAI = user?.canUseAI !== false;
      if (user?.jwt) {
        storeTokens(user.jwt, user.refreshToken);
      }
    } catch (error) {
      Logger.warn('Failed to load canUseAI; failing open', error);
      cachedCanUseAI = true;
    } finally {
      userPermissionLoaded = true;
      userPermissionPromise = null;
    }
    return cachedCanUseAI;
  })();

  return userPermissionPromise;
}

export async function loadAiAssistantGate() {
  await Promise.all([loadAiCreditSummary(), loadCanUseAI()]);
  return shouldShowAiAssistant();
}

export function openUsageLimitModal() {
  if (document.querySelector('.usage-limit-modal')) return;

  const overlay = document.createElement('div');
  overlay.className = 'usage-limit-modal';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'usage-limit-title');

  overlay.innerHTML = `
    <div class="usage-limit-modal__panel">
      <h2 id="usage-limit-title" class="usage-limit-modal__title">${t('ai_usage_limit_title')}</h2>
      <div class="usage-limit-modal__body">
        <p>${t('ai_usage_limit_body_1')}</p>
        <p>${t('ai_usage_limit_body_2')}</p>
      </div>
      <button type="button" class="usage-limit-modal__ok">${t('ai_usage_limit_ok')}</button>
    </div>
  `;

  const close = () => overlay.remove();
  overlay.querySelector('.usage-limit-modal__ok').onclick = close;
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  document.body.appendChild(overlay);
}
